import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";

import styles from "./styles.module.css";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import { Order, Task } from "@/types";
import { Grid } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Divider from "@/components/atoms/Divider";
import {
  FinalAmountFormInput,
  FinalAmountValidationSchema,
} from "@/components/pages/Tasks/validation/finalAmountValidation";
import { postFinalAmount } from "@/services/Orders";
import { useAuth } from "@/contexts/AuthContext";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import DrawerAccordion from "@/components/atoms/accordions/DrawerAccordion";
import { useGetContractById } from "@/services/Contracts";
import NumberInput from "@/components/atoms/NumberInput";
import Badge from "@/components/atoms/Badge";
import OrderProductDetails from "@/components/molecules/DrawerDetails/OrderProductDetails";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

interface FinalAmountTaskProps {
  task: Task;
  onClose: () => void;
  order?: Order;
}

const FinalAmountTask: FC<FinalAmountTaskProps> = ({ task, onClose, order }) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { currentCompany, user } = useAuth();
  const { data: contract } = useGetContractById(order?.contractId as number, {
    enabled: !!order?.contractId,
  });

  const formId = "final-amount-form";

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<FinalAmountFormInput>({
    resolver: zodResolver(FinalAmountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      amount: order?.amount || 0,
    },
  });

  const { mutateAsync: mutateSingleFinalAmount, isPending } = useMutation({
    mutationFn: async ({ orderId, amount, taskId }: { orderId: number; amount: number; taskId: number }) => {
      await postFinalAmount(orderId, amount, taskId);
    },
    onSuccess: async () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.finalAmountSet"))
      );
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
      onClose();
    },
    onError: async () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.finalAmountSetError"))
      );
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
    },
  });

  const onSubmit = async (data: FinalAmountFormInput) => {
    if (!order) return;
    const amount = data.amount;
    const originalAmount = order.amount;
    const amountDeviation = order.amountDeviation || 0;

    const deviation: number = (originalAmount / 100) * amountDeviation;
    const lowEnd: number = originalAmount - deviation;
    const highEnd: number = originalAmount + deviation;

    if (amount > highEnd || amount < lowEnd) {
      setError(`amount`, {
        type: "manual",
        message: t("tasks:legalFinalAmount") + lowEnd + order.unit?.unit + " - " + highEnd + order.unit?.unit,
      });
      return;
    }
    await mutateSingleFinalAmount({
      orderId: order.id,
      amount: amount,
      taskId: task.id,
    });
  };

  return (
    <DrawerTabs defaultValue="orders" className="full flex col between h-100 py-10">
      <Group gap="20px" align="center" className={styles.taskHeader}>
        <SVGAsset name={order?.orderType.type === "BUY" ? "purchaseIndicator" : "saleIndicator"} />
        <Text size="md" display="technical" tt="uppercase" strong={true}>
          {t("enumerables:taskTypes." + task?.type)}
        </Text>
      </Group>
      {/* Form with ID to connect to external submit button */}
      <DrawerTabs.List>
        <DrawerTabs.Tab value="orders">
          <DrawerTab svgName="hamburger" label={t("tasks:orders")} />
        </DrawerTabs.Tab>
      </DrawerTabs.List>

      <DrawerTabs.Panel value="orders" className={styles.tabPanel}>
        {order && (
          <form id={formId} onSubmit={handleSubmit(onSubmit)} className={styles.tabPanel}>
            <DrawerAccordion unstyled={true} defaultValue={"order"}>
              <DrawerAccordion.Item value={"order"}>
                <DrawerAccordion.Control>
                  <Text size="sm" display="technical" tt="uppercase" strong={true}>
                    {t("common:order")} {order?.id}
                  </Text>
                </DrawerAccordion.Control>
                <DrawerAccordion.Panel>
                  <Grid>
                    <OrderProductDetails
                      defaultProductName={`${order.salesItem.nameTranslated?.[user?.language.id as number] ?? order.salesItem.salesItem} - ${order.properties[0] ? order.properties[0].value : ""}`}
                      product={contract?.contractProduct.companySpecificProduct}
                    />
                    <Grid.Col>
                      <Divider />
                    </Grid.Col>
                    <FinalAmount order={order} control={control} isValid={isValid} isLoading={isPending} />
                  </Grid>
                </DrawerAccordion.Panel>
              </DrawerAccordion.Item>
            </DrawerAccordion>
          </form>
        )}
      </DrawerTabs.Panel>
    </DrawerTabs>
  );
};

export default FinalAmountTask;

interface FinalAmountProps {
  order: Order;
  control: any;
  isValid: boolean;
  isLoading: boolean;
}

const FinalAmount: React.FC<FinalAmountProps> = ({ order, control, isValid, isLoading }) => {
  const { t } = useTranslation();

  return (
    <>
      <Grid.Col span={3}>
        <Text size="sm" c="dimmed">
          {t("tasks:finalAmount")}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        <Group>
          <Controller
            control={control}
            name={"amount"}
            render={({ field, fieldState }) => {
              return (
                <Group justify={"flex-start"} align={"flex-start"}>
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      overflow: "hidden",
                      maxWidth: 190,
                    }}
                  >
                    <NumberInput
                      id={"amount"}
                      hideControls
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={
                        fieldState.error
                          ? fieldState.error.type === "manual"
                            ? fieldState.error.message
                            : t(fieldState.error.message as string)
                          : undefined
                      }
                    />
                    <Badge
                      size={"sm"}
                      h={35}
                      radius={0}
                      color={"primary"}
                      pos={"absolute"}
                      top={0}
                      right={0}
                      bottom={0}
                    >
                      {order.unit?.unit}
                    </Badge>
                  </span>

                  <CTA
                    data-testid="final-amount-submit-btn"
                    size="sm"
                    intent="secondary"
                    type={"submit"}
                    form="final-amount-form"
                    disabled={!isValid}
                    loading={isLoading}
                  >
                    {t("tasks:log")}
                  </CTA>
                </Group>
              );
            }}
          />
        </Group>
      </Grid.Col>
    </>
  );
};
